import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { client } from "@/api"

interface TeamMember {
  public_id: string
  name: string
  email: string
  role: string
  email_verified: boolean
  is_superuser: boolean
  suspended_at: string | null
}

interface TeamStats {
  memberCount: number
  gameCount: number
  clipCount: number
  videoCount: number
  storageUsedGB: number
}

interface TeamInvite {
  public_id: string
  email: string
  role: string
  status: string
  created_at: string
  expires_at: string
  inviterName: string
  // Slice 4: latest SES/Resend delivery state for this invite's most recent
  // send (raw email_events status), null when no send has been recorded.
  deliveryStatus?: "queued" | "sent" | "delivered" | "bounced" | "complained" | "failed" | null
  lastEventAt?: string | null
}

export interface TeamExchangeLink {
  public_id: string
  status: "pending" | "accepted" | "revoked" | "declined"
  type: "one_way" | "two_way"
  recipient_email: string
  include_annotations: boolean
  hidden_column_keys: string[]
  includes_game_data: boolean
  can_download: boolean
  message: string | null
  created_at: string
  accepted_at: string | null
  revoked_at: string | null
  source_game_public_id: string
  source_game_title: string | null
  source_game_date: string | null
  sender_name: string | null
  accept_url: string
  download_url: string
}

interface TeamDetail {
  team: {
    public_id: string
    name: string
    created_at: string
    // null = never set; behaves as US.
    country: "us" | "ca" | null
    // "What do you play?"; null = never set, read through the country.
    game?: string | null
  }
  members: TeamMember[]
  stats: TeamStats
  invites?: TeamInvite[]
  featureFlags?: FeatureFlagInfo[]
  exchanges?: TeamExchangeLink[]
}

export interface FeatureFlagInfo {
  key: string
  name: string
  description: string | null
  evaluationLevel: string
  defaultValue: boolean
  enabled: boolean
  source: string
  teamOverride: boolean | null
}

export function useTeamDetail(teamId: string) {
  return useQuery<TeamDetail>({
    queryKey: ["team", teamId],
    queryFn: async () => {
      const [teamResponse, invitesResponse, flagsResponse, exchangesResponse] = await Promise.all([
        client.admin.teams[":publicId"].$get({ param: { publicId: teamId } }),
        client.admin.teams[":publicId"].invites.$get({ param: { publicId: teamId } }),
        client.admin.teams[":publicId"]["feature-flags"].$get({ param: { publicId: teamId } }),
        client.admin.teams[":publicId"].exchanges.$get({ param: { publicId: teamId } }),
      ])

      if (!teamResponse.ok) {
        throw new Error("Failed to fetch team details")
      }

      const teamData = await teamResponse.json()
      
      let invites: any[] = []
      if (invitesResponse.ok) {
        const invitesData = await invitesResponse.json()
        invites = invitesData.invites || []
      }

      let featureFlags: FeatureFlagInfo[] = []
      if (flagsResponse.ok) {
        const flagData = (await flagsResponse.json()) as { flags?: FeatureFlagInfo[] }
        featureFlags = flagData.flags || []
      }

      let exchanges: TeamExchangeLink[] = []
      if (exchangesResponse.ok) {
        const exchangeData = (await exchangesResponse.json()) as { exchanges?: TeamExchangeLink[] }
        exchanges = exchangeData.exchanges || []
      }

      return {
        ...teamData,
        invites,
        featureFlags,
        exchanges,
      }
    },
    enabled: !!teamId,
  })
}

export function useUpdateTeamMember() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ teamId, userId, role }: { teamId: string; userId: string; role: string }) => {
      const response = await (client.admin.teams[":publicId"].members[":userPublicId"] as any).$put({
        param: { publicId: teamId, userPublicId: userId },
        json: { role },
      })
      
      if (!response.ok) {
        throw new Error("Failed to update team member")
      }
      
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] })
    },
  })
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ teamId, userId }: { teamId: string; userId: string }) => {
      const response = await client.admin.teams[":publicId"].members[":userPublicId"].$delete({
        param: { publicId: teamId, userPublicId: userId },
      })
      
      if (!response.ok) {
        throw new Error("Failed to remove team member")
      }
      
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] })
    },
  })
}

export function useResendInvite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      teamId,
      invitePublicId,
    }: {
      teamId: string
      invitePublicId: string
    }) => {
      const response = await (
        client.admin.teams[":publicId"].invites[":invitePublicId"].resend as any
      ).$post({
        param: { publicId: teamId, invitePublicId },
      })

      if (!response.ok) {
        const err = (await response.json().catch(() => ({}))) as { message?: string }
        throw new Error(err.message || "Failed to resend invitation")
      }

      return response.json() as Promise<{ success: boolean; email: string; expires_at?: string }>
    },
    onSettled: (_, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] })
    },
  })
}

export function useResendAllInvites() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ teamId }: { teamId: string }) => {
      const response = await (
        client.admin.teams[":publicId"].invites["resend-all"] as any
      ).$post({
        param: { publicId: teamId },
      })

      if (!response.ok) {
        const err = (await response.json().catch(() => ({}))) as { message?: string }
        throw new Error(err.message || "Failed to resend invitations")
      }

      return response.json() as Promise<{ total: number; sent: number }>
    },
    onSettled: (_, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] })
    },
  })
}

export function useInviteTeamMember() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ teamId: _teamId, email: _email, role: _role }: { teamId: string; email: string; role: string }) => {
      // Note: Admin routes don't have POST for invites, would need to use v1 API
      // For now, this functionality is not available in admin panel
      throw new Error("Invite functionality not implemented in admin panel")
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] })
    },
  })
}

export function useUpdateTeamFeatureFlag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      teamId,
      flagKey,
      enabled,
    }: {
      teamId: string
      flagKey: string
      enabled: boolean
    }) => {
      const response = await (
        client.admin.teams[":publicId"]["feature-flags"][":flagKey"] as any
      ).$put({
        param: { publicId: teamId, flagKey },
        json: { enabled },
      })

      if (!response.ok) {
        throw new Error("Failed to update feature flag")
      }

      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] })
    },
  })
}
